<template>
    <div class="visitor-attributes">
        <div class="attribute w-full" v-for="attribute in getAttributes()">
            <span class="material-icons" style="font-size: 15px">{{ attribute.icon }}</span>
            <span class="name">{{ attribute.label }}</span>
            <div class="value w-full" :class="{ empty: !visitor[attribute.name] }">
                <div class="edited-input" v-if="isContentEditable(attribute) && attribute.name == editingProperty">
                    <a-input
                        class="w-full"
                        v-model="editingContent"
                        @mouseleave="editingProperty = null"
                        @change="changeAttr(attribute, $event.target.value)"
                    >
                    </a-input>
                </div>
                <div
                    class="edited-input"
                    v-tippy="{
                        followCursor: true,
                        placement: 'bottom-end',
                    }"
                    :content="getData(visitor, attribute) || 'N/A'"
                    @mouseover="
                        editingProperty = attribute.name;
                        editingContent = getData(visitor, attribute) == 'N/A' ? '' : getData(visitor, attribute);
                    "
                    v-else
                >
                    {{ getData(visitor, attribute) || "N/A" }}
                </div>
                <span class="material-icons mr-1" v-if="isContentEditable(attribute)">edit</span>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { log2 } from "../../../utils/log2";

const log = log2("ChatBar");
export default {
    name: "VisitorAttributes",
    props: {
        visitor: {
            type: Object,
            default: () => ({}),
        },
        updatingVisitor: {
            details: {},
        },
    },
    data() {
        return {
            staticAttributes: {
                _id: "badge",
                fullname: "person",
                email: "alternate_email",
                browser: "public",
                city: "public",
                connectionTime: "date_range",
                country: "alternate_email",
                createdAt: "date_range",
                instagramInfo: "badge",
                instagramUserId: "badge",
                messengerUserId: "badge",
                ipAddress: "computer",
                os: "settings_suggest",
                platform: "alternate_email",
                preferredLang: "alternate_email",
                siteId: "alternate_email",
                status: "alternate_email",
                telegramInfo: "alternate_email",
                telegramUserId: "alternate_email",
                updatedAt: "alternate_email",
                visitingPage: "public",
                whatsappNumber: "public",
                blockBy: "public",
                isBlocked: "public",
            },
            editableContent: ["fullname", "email", "city", "country", "preferredLang"],
            editTimeout: null,
            editingProperty: null,
            editingContent: null,
        };
    },
    computed: {
        ...mapState({
            visitorAttributes: (state) => state.contactProperty.data || [],
        }),
    },
    methods: {
        ...mapActions({
            updateContactProperty: "visitor/updateContactProperty",
            updateVisitorAttr: "visitor/updateVisitorAttr",
        }),
        getData(visitor, attr) {
            let data = visitor?.[attr.name];
            if (attr.id) {
                data = (visitor.contactProperties || []).find((c) => c.property?._id == attr.id)?.value;
            }
            return data;
        },
        isContentEditable(attr) {
            return this.editableContent.includes(attr.name) || attr.id
                ? !this.staticAttributes[attr.id]
                : !this.staticAttributes[attr.name];
        },
        getAttributes() {
            let newAttributes = [
                ...Object.keys(this.staticAttributes).map((attribute) => ({
                    label: this.$_.startCase(attribute),
                    name: attribute,
                    editable: false,
                    icon: this.staticAttributes[attribute],
                })),
                ...this.visitorAttributes.map((attribute) => ({
                    id: attribute._id,
                    label: attribute.label,
                    name: attribute.name,
                    editable: true,
                    icon: "tag",
                })),
            ];
            return newAttributes;
        },
        changeAttr(attr, value) {
            if (this.editTimeout) clearTimeout(this.editTimeout);
            if (this.visitor) {
                this.editTimeout = setTimeout(() => {
                    if (this.staticAttributes[attr.name]) {
                        this.updateVisitorAttr({
                            visitorId: this.visitor._id,
                            payload: {
                                [attr.name]: value,
                            },
                        }).then(() => {
                            this.visitor[attr.name] = value;
                        });
                    } else {
                        this.updateContactProperty({
                            visitor: this.visitor,
                            contactPropertyId: attr.id,
                            updatePayload: {
                                value,
                            },
                        }).then((v) => (this.visitor = v));
                    }
                    this.$message.success({
                        content: this.$t("message.settingsSaved"),
                        duration: 2,
                        key: "saved",
                    });
                }, 1000);
            }
        },
    },
};
</script>
<style lang="scss">
.editable-content {
    .material-icons {
        margin-left: 4px;
        font-size: 15px;
    }
}
</style>
<style lang="scss" scoped>
.visitor-attributes {
    border: 1px solid #dbe1e8;
    border-radius: 4px;
    -webkit-box-shadow: 0 1px 1px 0 rgb(0 0 0 / 13%);
    box-shadow: 0 1px 1px 0 rgb(0 0 0 / 13%);
    padding: 25px;
    display: grid;
    width: 100%;
    min-width: 500px;
    overflow: hidden;

    .attribute {
        height: 36px;
        display: flex;
        align-items: center;
        overflow: hidden;
    }

    .more-button {
        border: none;
        text-align: left;
        font-weight: 600;
    }

    .name {
        margin-left: 10px;
        width: 200px;
        color: #516173;
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
    }

    .value {
        color: #22272d;
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        margin-left: 25px;
        width: 100%;
        display: flex;
        align-items: center;

        .material-icons {
            font-size: 16px;
            margin-left: 10px;
        }

        .edited-input {
            text-overflow: ellipsis;
            overflow: hidden;
            width: 100%;
            white-space: nowrap;
        }

        &.empty {
            color: #516173;
        }

        i {
            color: var(--def);
            margin-left: 5px;
        }
    }

    .tags {
        display: flex;
        gap: 5px;
        flex-wrap: wrap;

        .tag {
            border: 1px solid #f2f2f2;
            border-radius: 4px;
            padding: 5px 10px;
            user-select: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            color: #22272d;
            font-weight: 500;
            font-size: 14px;
            line-height: 20px;

            .tag-color {
                width: 15px;
                height: 15px;
                border-radius: 50%;
                display: inline-block;
                margin-right: 5px;
            }
        }
    }
}
</style>
